/**
 * Single source of truth for the four lenses (personas).
 * Presentation-layer config only — the persona KEYS are a contract with the
 * council-chat edge function and must never change.
 */

export type Persona = "krishna" | "chanakya" | "vidur" | "shriram";

export interface LensConfig {
  /** Contract key — must match the edge function's VALID_PERSONAS */
  id: Persona;
  name: string;
  /** Compact label for tight UI where the full name overflows */
  shortName: string;
  /** Short posture line shown under the name */
  subtitle: string;
  initial: string;
  /** Devanagari accent glyph (rendered in font-dev) */
  devanagari: string;
  /** Persona pigment — dots, rules and large glyphs only. Never behind small text. */
  color: string;
  description: string;
  contextLine: string;
  /** Source tradition, elevated in the counsel header + source block */
  textName: string;
  tradition: string;
  /** Fallback stance word used before the lens has actually answered */
  posture: string;
  /** Framework label shown on council cards */
  framework: string;
  /** Short marketing line */
  oneLiner: string;
  /** Tonal pair, e.g. "Calm · Reflective" */
  tonal: string;
  /** Long-form "Draws from …" sentence (counsel interstitial) */
  traditionSentence: string;
  /** Longer descriptive line used on the marketing lens scene */
  voiceSample: string;
  /** CSS variable name for the lens accent (defined in index.css) */
  accentVar: string;
}

export const LENSES: Record<Persona, LensConfig> = {
  krishna: {
    id: "krishna",
    name: "Bhagavad Gita lens",
    shortName: "Gita lens",
    subtitle: "Calm the noise",
    initial: "G",
    devanagari: "गी",
    color: "hsl(var(--lens-krishna))",
    description: "When you're stuck in your head and need to see the situation clearly",
    contextLine: "Through the Bhagavad Gita lens — clarity of purpose, and detachment from the outcome.",
    textName: "Bhagavad Gita",
    tradition: "Dharma, detachment, and devoted action",
    posture: "Clarity",
    framework: "Dharma & Devotion",
    oneLiner: "For clarity under pressure.",
    tonal: "Calm · Reflective",
    traditionSentence: "Draws from the Bhagavad Gita's philosophy of dharma, detachment, and devoted action.",
    voiceSample: "For clarity under pressure, emotional balance, and seeing the bigger picture.",
    accentVar: "--lens-krishna",
  },
  chanakya: {
    id: "chanakya",
    name: "Chanakya",
    shortName: "Chanakya",
    subtitle: "Real-world strategy",
    initial: "C",
    devanagari: "चा",
    color: "hsl(var(--lens-chanakya))",
    description: "When it's about money, work, power, or a move you need to make",
    contextLine: "Through the Chanakya lens — strategy, leverage, and practical moves.",
    textName: "Arthashastra",
    tradition: "Strategy, statecraft, and pragmatic power",
    posture: "Leverage",
    framework: "Strategy & Power",
    oneLiner: "For strategy and smart moves.",
    tonal: "Sharp · Strategic",
    traditionSentence: "Draws from the Arthashastra's frameworks of strategy, statecraft, and pragmatic power.",
    voiceSample: "For strategy, negotiation, leverage, and smart career moves.",
    accentVar: "--lens-chanakya",
  },
  vidur: {
    id: "vidur",
    name: "Vidur",
    shortName: "Vidur",
    subtitle: "The long view",
    initial: "V",
    devanagari: "वि",
    color: "hsl(var(--lens-vidur))",
    description: "When the decision will play out over years, not weeks",
    contextLine: "Through the Vidur lens — long-term consequence and steady judgment.",
    textName: "Mahabharata",
    tradition: "Foresight, ethics, and long consequence",
    posture: "Foresight",
    framework: "Narrative & Meaning",
    oneLiner: "For honest judgment.",
    tonal: "Balanced · Honest",
    traditionSentence: "Draws from the Mahabharata's deep counsel on foresight, ethics, and multi-stakeholder wisdom.",
    voiceSample: "For honest judgment, difficult situations, and long-term consequences.",
    accentVar: "--lens-vidur",
  },
  shriram: {
    id: "shriram",
    name: "Ramayan lens",
    shortName: "Ramayan lens",
    subtitle: "Hold your line",
    initial: "R",
    devanagari: "रा",
    color: "hsl(var(--lens-ram))",
    description: "When you already know the right thing and the cost feels heavy",
    contextLine: "Through the Ramayan lens — duty, integrity, and the harder right thing.",
    textName: "Ramayan",
    tradition: "Duty, integrity, and the harder right thing",
    posture: "Integrity",
    framework: "Integrity & Values",
    oneLiner: "For duty and integrity.",
    tonal: "Grounded · Principled",
    traditionSentence: "Draws from the Ramayan's ideals of dharmic duty, integrity, and righteous action.",
    voiceSample: "For duty, integrity, and decisions that must stay clean even when life is messy.",
    accentVar: "--lens-ram",
  },
};

export const LENS_ORDER: Persona[] = ["krishna", "chanakya", "vidur", "shriram"];

export function isPersona(v: string | null | undefined): v is Persona {
  return !!v && Object.prototype.hasOwnProperty.call(LENSES, v);
}

/** Full-strength pigment for dots, rules, borders and large glyphs. */
export function lensColor(id: Persona): string {
  return `hsl(var(${LENSES[id].accentVar}))`;
}

/** Translucent pigment fill for avatar circles. */
export function avatarBg(id: Persona): string {
  return `hsl(var(${LENSES[id].accentVar}) / 0.15)`;
}

/** Card border pigment. */
export function borderColor(id: Persona): string {
  return lensColor(id);
}

/** Grammatical "… lens" phrase — appends "lens" only when the name lacks it. */
export function lensPhrase(id: Persona): string {
  const n = LENSES[id].name;
  return n.endsWith("lens") ? n : `${n} lens`;
}

/** Compact "… lens" phrase for tight UI. */
export function shortLensPhrase(id: Persona): string {
  const n = LENSES[id].shortName;
  return n.endsWith("lens") ? n : `${n} lens`;
}

/** "Bhagavad Gita lens" — source label used on the marketing lens scene. */
export function sourceLabel(id: Persona): string {
  return `${LENSES[id].textName} lens`;
}

/** Pigment at a given alpha — safe with the hsl(var(--…)) token form. */
export function lensAlpha(id: Persona, alpha: number): string {
  return `hsl(var(${LENSES[id].accentVar}) / ${alpha})`;
}
